import {matrixService} from '../../../services/matrixService';
import type {IncomingChatWebhook} from './incomingChatWebhookApi';
import type {Webhook, WebhookDelivery, WebhookDeliveryStats, WebhookDeliveryStatus} from '../../../store/features/integrations/integrationsService';

export type WebhookState = 'active' | 'paused' | 'failing';

export interface RoomInfo {
    readonly name: string;
    readonly encrypted: boolean;
    readonly members: number | undefined;
}

// The backend only knows m.room.name, which direct rooms lack; the chat client resolves the same names the room list shows.
export const roomInfo = (roomId: string, fallback?: {readonly name: string; readonly encrypted: boolean}): RoomInfo => {
    const room = matrixService.getClient()?.getRoom(roomId);
    return {
        name: room?.name || fallback?.name || roomId,
        encrypted: room?.hasEncryptionStateEvent() ?? fallback?.encrypted ?? false,
        members: room?.getJoinedMemberCount(),
    };
};

export const webhookState = (hook: IncomingChatWebhook): WebhookState =>
    hook.status !== 'enabled' ? 'paused' : hook.attention ? 'failing' : 'active';

const ATTENTION_MESSAGES: Readonly<Record<string, string>> = {
    bot_not_joined: 'The PhoneHQ Webhooks participant is no longer in the room. Re-invite it to resume delivery.',
    room_forbidden: 'The PhoneHQ Webhooks participant lost permission to post in this room.',
    room_not_found: 'The destination room no longer exists.',
    expired: 'Some messages expired before they could be delivered.',
};

export const attentionMessage = (code: string): string =>
    ATTENTION_MESSAGES[code] ?? `Recent deliveries failed (${code.replaceAll('_', ' ')}).`;

const RELATIVE_UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [['day', 86_400], ['hour', 3_600], ['minute', 60]];
const relativeFormat = new Intl.RelativeTimeFormat('en', {numeric: 'auto', style: 'short'});

export const relativeTime = (value: string | null): string => {
    if (!value) return '—';
    const seconds = (new Date(value).getTime() - Date.now()) / 1000;
    const unit = RELATIVE_UNITS.find(([, size]) => Math.abs(seconds) >= size);
    return unit ? relativeFormat.format(Math.round(seconds / unit[1]), unit[0]) : 'Just now';
};

export const absoluteTime = (value: string | null): string => value ? new Date(value).toLocaleString() : 'No messages yet';

// Keeps the public prefix recognisable while hiding the credential path segments.
export const maskWebhookUrl = (url: string): string => url.replace(/(\/incoming\/[^/]+\/).*$/, '$1••••••••••••••••••••••••');

export const SECTION_LABEL = 'text-[11px] font-semibold uppercase tracking-wide text-ink-muted';

const DAY_MS = 86_400_000;
const RETENTION_DAYS = 30;

export const EVENT_OPTIONS = [
    {value: 'CALL_ANSWERED', label: 'Call answered', description: 'A call on one of your numbers is picked up.'},
    {value: 'CALL_MISSED', label: 'Missed call', description: 'An inbound call goes unanswered.'},
    {value: 'CALL_ENDED', label: 'Call ended', description: 'A call finishes.'},
] as const;

const EVENT_LABELS: Readonly<Record<string, string>> = {
    ...Object.fromEntries(EVENT_OPTIONS.map(option => [option.value, option.label])),
    USER_CREATED: 'User created',
    WEBHOOK_TEST: 'Webhook test',
};

export const eventTypeLabel = (type: string): string =>
    EVENT_LABELS[type] ?? type.charAt(0) + type.slice(1).toLowerCase().replaceAll('_', ' ');

const DATE_LOCALE = 'en-GB';

export const shortDateTime = (value: string | null): string =>
    value ? new Date(value).toLocaleString(DATE_LOCALE, {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'}) : '—';

export const shortDate = (value: string | null): string =>
    value ? new Date(value).toLocaleDateString(DATE_LOCALE, {month: 'short', day: 'numeric'}) : '—';

export const dateTime = (value: string | null): string => value ? new Date(value).toLocaleString(DATE_LOCALE) : '—';

export type WebhookKind = 'healthy' | 'failing' | 'autoDisabled' | 'off';

export const webhookKind = ({health}: Pick<Webhook, 'health'>): WebhookKind =>
    health.state === 'DISABLED' ? (health.disabledReason === 'AUTO_FAILING' ? 'autoDisabled' : 'off')
        : health.state === 'FAILING' ? 'failing' : 'healthy';

export type WebhookFilter = 'all' | 'healthy' | 'failing' | 'off';

export const WEBHOOK_FILTERS: readonly (readonly [WebhookFilter, string])[] = [['all', 'All'], ['healthy', 'Healthy'], ['failing', 'Failing'], ['off', 'Off']];

export const matchesWebhookFilter = (kind: WebhookKind, filter: WebhookFilter): boolean =>
    filter === 'all' || (filter === 'off' ? kind === 'off' || kind === 'autoDisabled' : kind === filter);

export const matchesWebhookQuery = ({name, url}: Pick<Webhook, 'name' | 'url'>, query: string): boolean =>
    `${name} ${url}`.toLowerCase().includes(query.trim().toLowerCase());

export const endpointLabel = (url: string): string => url.replace(/^https?:\/\//, '');

const failingDays = ({failingSince, disabledAt}: Webhook['health']): number | undefined =>
    failingSince && disabledAt ? Math.round((new Date(disabledAt).getTime() - new Date(failingSince).getTime()) / DAY_MS) : undefined;

export const healthNote = (webhook: Pick<Webhook, 'health' | 'recentDeliveries'>): string => {
    const {health} = webhook;
    const recent = webhook.recentDeliveries ?? [];
    switch (webhookKind(webhook)) {
        case 'healthy':
            return health.lastSuccessAt ? `Last delivery ${relativeTime(health.lastSuccessAt)}`
                : recent.length > 0 ? 'No successful deliveries yet' : 'No deliveries yet';
        case 'failing': {
            const failed = recent.filter(status => status === 'FAILED').length;
            return failed > 0 ? `${failed} of the last ${recent.length} failed` : `Failing since ${shortDateTime(health.failingSince)}`;
        }
        case 'autoDisabled': {
            const days = failingDays(health);
            return `Turned off ${shortDate(health.disabledAt)} after ${days ? `${days} ${days === 1 ? 'day' : 'days'} of failures` : 'repeated failures'}`;
        }
        case 'off':
            return 'Turned off manually';
    }
};

export type TickTone = 'success' | 'failure' | 'pending';

export const tickTone = (status: WebhookDeliveryStatus): TickTone =>
    status === 'SUCCEEDED' ? 'success' : status === 'FAILED' ? 'failure' : 'pending';

export interface ScopeSummary {
    readonly label: string;
    readonly detail: string;
}

export const scopeSummary = ({eventTypes, phoneNumbers}: Pick<Webhook, 'eventTypes' | 'phoneNumbers'>): ScopeSummary => {
    const scope = eventTypes.find(eventType => eventType.phoneNumbers)?.phoneNumbers;
    if (!scope) return {label: '—', detail: ''};
    if (scope.type === 'ALL') return {label: 'All phone numbers', detail: ''};
    const ids = scope.phoneNumberIds ?? [];
    const names = (phoneNumbers ?? []).filter(number => ids.includes(number.msisdnId)).map(number => number.label || String(number.msisdn));
    return {label: `${ids.length} ${ids.length === 1 ? 'number' : 'numbers'}`, detail: names.join(', ')};
};

export const retryFailedSince = ({health}: Pick<Webhook, 'health'>, now = Date.now()): string =>
    new Date(Math.max(health.failingSince ? new Date(health.failingSince).getTime() : 0, now - RETENTION_DAYS * DAY_MS)).toISOString();

export type DeliveryKind = 'delivered' | 'failed' | 'retrying' | 'queued' | 'sending' | 'waiting';

export type DeliveryKindInput = Pick<WebhookDelivery, 'status' | 'attempts'> & {readonly waitingFor?: WebhookDelivery['waitingFor']};

export const deliveryKind = ({status, attempts, waitingFor}: DeliveryKindInput): DeliveryKind =>
    status === 'SUCCEEDED' ? 'delivered'
        : status === 'FAILED' ? 'failed'
            : status === 'SENDING' ? 'sending'
                : waitingFor ? 'waiting'
                    : attempts > 0 ? 'retrying' : 'queued';

const DELIVERY_LABELS: Readonly<Record<Exclude<DeliveryKind, 'waiting'>, string>> = {
    delivered: 'Delivered',
    failed: 'Failed',
    retrying: 'Retrying',
    queued: 'Queued',
    sending: 'Sending',
};

export const deliveryLabel = (delivery: DeliveryKindInput): string => {
    const kind = deliveryKind(delivery);
    return kind === 'waiting'
        ? delivery.waitingFor === 'CALLSCRIBE' ? 'Waiting for transcript' : 'Waiting for recording'
        : DELIVERY_LABELS[kind];
};

export const isDeliveryInFlight = ({status}: Pick<WebhookDelivery, 'status'>): boolean => status === 'PENDING' || status === 'SENDING';

export const isDeliveryRetryable = ({status}: Pick<WebhookDelivery, 'status'>): boolean => status === 'SUCCEEDED' || status === 'FAILED';

const dueLabel = (value: string | null): string => {
    const seconds = value ? (Date.parse(value) - Date.now()) / 1000 : 0;
    return seconds <= 0 ? 'Now' : seconds < 60 ? 'In a few seconds' : relativeTime(value);
};

export const nextRetryLabel = (delivery: DeliveryKindInput & Pick<WebhookDelivery, 'nextAttemptAt'>): string => {
    const kind = deliveryKind(delivery);
    return kind === 'queued' ? 'Now' : kind === 'retrying' || kind === 'waiting' ? dueLabel(delivery.nextAttemptAt) : '—';
};

export type DeliveryFilter = 'all' | 'failed' | 'retrying' | 'delivered';

export const DELIVERY_FILTERS: readonly {readonly id: DeliveryFilter; readonly label: string; readonly statuses?: WebhookDeliveryStatus[]}[] = [
    {id: 'all', label: 'All'},
    {id: 'failed', label: 'Failed', statuses: ['FAILED']},
    {id: 'retrying', label: 'Retrying', statuses: ['PENDING', 'SENDING']},
    {id: 'delivered', label: 'Delivered', statuses: ['SUCCEEDED']},
];

export const deliveryFilterCount = (filter: DeliveryFilter, stats: WebhookDeliveryStats): number => {
    switch (filter) {
        case 'all': return stats.pending + stats.sending + stats.succeeded + stats.failed;
        case 'failed': return stats.failed;
        case 'retrying': return stats.pending + stats.sending;
        case 'delivered': return stats.succeeded;
    }
};

export const DELIVERY_RANGES = [[1, 'Last 24 hours'], [7, 'Last 7 days'], [RETENTION_DAYS, 'Last 30 days']] as const;

export const rangeStart = (days: number, now = Date.now()): string => new Date(now - days * DAY_MS).toISOString();

const HTTP_REASONS: Readonly<Record<number, string>> = {
    200: 'OK', 201: 'Created', 202: 'Accepted', 204: 'No Content', 301: 'Moved Permanently', 302: 'Found',
    400: 'Bad Request', 401: 'Unauthorized', 403: 'Forbidden', 404: 'Not Found', 405: 'Method Not Allowed', 408: 'Request Timeout',
    410: 'Gone', 413: 'Payload Too Large', 422: 'Unprocessable Entity', 429: 'Too Many Requests',
    500: 'Internal Server Error', 502: 'Bad Gateway', 503: 'Service Unavailable', 504: 'Gateway Timeout',
};

export const httpStatusLine = (code: number): string => HTTP_REASONS[code] ? `${code} ${HTTP_REASONS[code]}` : `HTTP ${code}`;

const DELIVERY_ERRORS: Readonly<Record<string, string>> = {
    TIMEOUT: 'Timed out',
    DNS_FAILED: 'DNS lookup failed',
    CONNECT_FAILED: 'Connection failed',
    TLS_FAILED: 'TLS handshake failed',
    BLOCKED_URL: 'URL blocked',
    INVALID_URL: 'Invalid URL',
    IO_FAILED: 'Network error',
    WEBHOOK_DISABLED: 'Webhook disabled',
};

export const deliveryError = (code: string | null): string => {
    if (!code) return '';
    const http = /^HTTP_(\d+)$/.exec(code);
    return http ? httpStatusLine(Number(http[1])) : DELIVERY_ERRORS[code] ?? code.replaceAll('_', ' ');
};

export const responseText = (statusCode: number | null, error: string | null): string =>
    error && !error.startsWith('HTTP_') ? deliveryError(error)
        : statusCode !== null ? httpStatusLine(statusCode) : deliveryError(error) || '—';

export const isSuccessCode = (statusCode: number | null): boolean => statusCode !== null && statusCode >= 200 && statusCode < 300;

export const errorStatus = (error: unknown): unknown => typeof error === 'object' && error !== null && 'status' in error ? error.status : undefined;

export const stableHeaders = (deliveryId: string): readonly (readonly [string, string])[] => [
    ['content-type', 'application/json'],
    ['user-agent', 'PhoneHQ-Webhooks/1.0'],
    ['webhook-id', deliveryId],
    ['X-Idempotency-Key', deliveryId],
];
